import { motion } from "framer-motion";
import { Reveal, MaskedLine, EASE } from "@/components/motion/Reveal";
import useSEO from "@/lib/useSEO";
import { useSettings } from "@/context/SettingsContext";
import { resolveMedia } from "@/lib/api";

const PORTRAIT = "https://images.unsplash.com/photo-1775654357297-e05891dfd4e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwZmVtYWxlJTIwYXJjaGl0ZWN0JTIwZGVzaWduZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODMzNDA2ODJ8MA&ixlib=rb-4.1.0&q=85";

export default function ChiSono() {
  useSEO({ title: "Chi Sono", description: "Giovanna Matino, interior designer a Napoli. Un approccio sartoriale alla progettazione d'interni, in Campania e online." });
  const { settings } = useSettings();
  const img = settings.about_image ? resolveMedia(settings.about_image) : PORTRAIT;
  const imgMobile = settings.about_image_mobile ? resolveMedia(settings.about_image_mobile) : null;
  const resp = settings.about_responsive || {};
  const alt = settings.about_alt || "Ritratto di Giovanna Matino";
  const focal = `${Number(settings.about_focal_x ?? 50)}% ${Number(settings.about_focal_y ?? 40)}%`;
  const zoom = Math.min(200, Math.max(100, Number(settings.about_zoom) || 100)) / 100;
  return (
    <div data-testid="page-chi-sono">
      <section className="container-x pt-10 pb-24">
        <motion.span
          className="eyebrow block"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          Chi Sono
        </motion.span>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] mt-6 max-w-3xl">
          <MaskedLine delay={0.15}>Giovanna Matino,</MaskedLine>
          <MaskedLine delay={0.32}><em className="text-gold font-light">interior designer.</em></MaskedLine>
        </h1>
      </section>

      <section className="container-x pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 relative">
            <motion.div
              className="absolute -top-4 -left-4 w-full h-full border border-gold/40 hidden md:block"
              aria-hidden
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.9, ease: EASE }}
            />
            <motion.div
              className="relative overflow-hidden"
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.1, delay: 0.3, ease: EASE }}
            >
              {settings.about_image ? (
                <picture>
                  {imgMobile && <source media="(max-width: 767px)" srcSet={imgMobile} />}
                  {resp["768"] && <source media="(max-width: 1024px)" srcSet={resolveMedia(resp["768"])} />}
                  {resp["1440"] && <source media="(max-width: 1600px)" srcSet={resolveMedia(resp["1440"])} />}
                  <img src={img} alt={alt} className="w-full h-[420px] md:h-[520px] object-cover"
                    style={{ objectPosition: focal, transform: zoom > 1 ? `scale(${zoom})` : "none" }}
                    loading="lazy" data-testid="about-image" />
                </picture>
              ) : (
                <img src={PORTRAIT} alt={alt} className="w-full h-[420px] md:h-[520px] object-cover" data-testid="about-image" />
              )}
            </motion.div>
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <Reveal delay={0.15}>
              <p className="font-serif text-3xl md:text-4xl leading-[1.35] text-ink">
                Ogni progetto nasce da un ascolto, prima ancora che da uno schizzo.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-8 space-y-6 text-taupe leading-[1.9] font-light">
                <p>
                  Sono Giovanna Matino, interior designer con studio a Napoli.
                  Da oltre dieci anni progetto spazi residenziali dove eleganza,
                  funzionalità e personalità convivono con equilibrio.
                </p>
                <p>
                  Il mio approccio è sartoriale: non impongo uno stile, ma lo costruisco
                  attorno alla vita di chi abiterà lo spazio. Studio la luce, seleziono
                  materiali onesti, curo ogni finitura come se fosse la mia.
                </p>
                <p>
                  Lavoro con clienti a Napoli, in tutta la Campania e online — perché
                  un progetto d'interni oggi non ha più confini geografici. Ogni casa
                  che progetto è unica, perché unica è la storia che racconta.
                </p>
              </div>
            </Reveal>
            <div className="mt-12 grid grid-cols-3 gap-6">
              {[["10+","Anni di esperienza"],["40","Progetti realizzati"],["5/5","Valutazione Google"]].map(([n,l], i)=>(
                <Reveal key={l} delay={0.3 + i * 0.12}>
                  <div className="font-serif text-4xl text-gold">{n}</div>
                  <div className="text-xs tracking-[0.14em] uppercase text-taupe mt-2">{l}</div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
